// provenance.client.ts — retient les paramètres du LIEN D'ARRIVÉE (utm, gclid,
// fbclid, paramètres perso de l'association) pour les webhooks envoyés à
// l'association à chaque paiement. Cf. utils/provenance.ts.
//
//   1. Arrivée sur le site (ou dans le widget) : paramètres du lien → mémoire.
//      Seul le lien d'arrivée compte : les paramètres internes du site
//      (recherche, filtres) ne remplacent jamais une provenance.
//   2. Lien interne qui recharge la page ou ouvre un onglet (bouton « Faire
//      un don » en target=_blank…) : la provenance voyage dans `prov`.
//   3. Pages de confirmation (don, adhésion, billetterie, boutique) : envoi,
//      rattaché aux références de la query. Les pages de don l'envoient en
//      plus AVANT le paiement (suivreProvenancePaiement).
//
// Conservation : mémoire de page + stockage de SESSION de l'onglet (effacé à
// sa fermeture), comme `donoor_attribution` (app.vue). Indispensable : un don
// MENSUEL Stripe recharge toute la page vers « Merci » (redirection du
// prestataire) — sans ce relais, la provenance était perdue. Le site hôte du
// widget est gardé de la même façon, pour que « Merci » puisse le prévenir.
// Jamais bloquant.

import { cheminSansLangue } from "~/utils/langue";
import { isValidHostOrigin } from "~/utils/embed";
import {
  PARAM_PAGE_PROVENANCE,
  PARAM_PROVENANCE,
  decoderProvenance,
  encoderProvenance,
  parametresDeProvenance,
  referencesDeConfirmation,
  CLE_SESSION_HOTE_WIDGET,
  type Provenance,
} from "~/utils/provenance";

const CLE_SESSION = "donoor_provenance_paiement";

function lireSession(cle: string): string | null {
  try {
    return window.sessionStorage.getItem(cle);
  } catch {
    return null;
  }
}
function ecrireSession(cle: string, valeur: string): void {
  try {
    window.sessionStorage.setItem(cle, valeur);
  } catch {
    /* stockage indisponible (navigation privée stricte) : mémoire seule */
  }
}

const PAGE_CONFIRMATION = /^\/(donation-success|a\/[^/]+\/success|b\/[^/]+\/success|boutique\/[^/]+\/success)\/?$/;

export default defineNuxtPlugin(() => {
  const provenance = useProvenance();
  const envoyer = creerEnvoiProvenance();
  const router = useRouter();

  // 1. Lien d'arrivée
  try {
    const query = Object.fromEntries(new URLSearchParams(window.location.search));
    const transportee = decoderProvenance(query[PARAM_PROVENANCE]);
    // Page de confirmation : ses paramètres (montant, e-mail…) ne sont jamais
    // une provenance — on reprend celle gardée pour l'onglet.
    const confirmation = PAGE_CONFIRMATION.test(cheminSansLangue(window.location.pathname));
    const parametres = Object.keys(transportee).length
      ? transportee
      : confirmation ? {} : parametresDeProvenance(query);
    if (Object.keys(parametres).length) {
      const page = query[PARAM_PAGE_PROVENANCE];
      provenance.value = {
        parametres,
        pageArrivee: page || `${window.location.origin}${window.location.pathname}`,
      };
      ecrireSession(CLE_SESSION, JSON.stringify(provenance.value));
    } else {
      // Rechargement complet (redirection d'un prestataire) : on reprend.
      const gardee = lireSession(CLE_SESSION);
      if (gardee) provenance.value = JSON.parse(gardee) as Provenance;
    }
    // Widget : site de l'association, pour la page « Merci » (après un paiement
    // par carte, Stripe recharge le widget vers « Merci »). Le widget de don
    // (/embed/…) ne porte pas `embed=1` : on reconnaît aussi son chemin.
    const dansWidget =
      query.embed === "1" || query.embed === "true" ||
      cheminSansLangue(window.location.pathname).startsWith("/embed/");
    if (dansWidget && isValidHostOrigin(query.host)) {
      ecrireSession(CLE_SESSION_HOTE_WIDGET, query.host);
    }
    // Adresse propre : `prov` ne doit pas se retrouver dans un lien partagé.
    if (query[PARAM_PROVENANCE] || query[PARAM_PAGE_PROVENANCE]) {
      const url = new URL(window.location.href);
      url.searchParams.delete(PARAM_PROVENANCE);
      url.searchParams.delete(PARAM_PAGE_PROVENANCE);
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    /* jamais bloquant */
  }

  // 2. Liens internes qui rechargent ou ouvrent un onglet
  const surClic = (evenement: MouseEvent) => {
    try {
      const p = provenance.value;
      if (!p || !Object.keys(p.parametres).length) return;
      const lien = (evenement.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!lien) return;
      const url = new URL(lien.href, window.location.href);
      if (url.origin !== window.location.origin || url.searchParams.has(PARAM_PROVENANCE)) return;
      url.searchParams.set(PARAM_PROVENANCE, encoderProvenance(p.parametres));
      if (p.pageArrivee) url.searchParams.set(PARAM_PAGE_PROVENANCE, p.pageArrivee);
      lien.href = url.toString();
    } catch {
      /* jamais bloquant */
    }
  };
  document.addEventListener("click", surClic, true);
  document.addEventListener("auxclick", surClic, true);

  // 3. Pages de confirmation
  const surPage = (chemin: string, query: Record<string, unknown>) => {
    if (PAGE_CONFIRMATION.test(cheminSansLangue(chemin))) envoyer(referencesDeConfirmation(query));
  };
  router.afterEach((vers) => surPage(vers.path, vers.query));
  surPage(router.currentRoute.value.path, router.currentRoute.value.query);
});
